import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import '@sendbird/uikit-react/dist/index.css';
import { SendBirdProvider } from '@sendbird/uikit-react';
import { useTheme } from '@mui/material';
import PropTypes from 'prop-types';

import { useAuth } from 'services/auth';
import { useApp } from 'contexts/AppContext';
import api from './api';

export const SendBirdReadyContext = createContext(false);

const SendBirdService = ({ children }) => {
    const theme = useTheme();
    const { state: user } = useAuth();
    const app = useApp();

    const [ready, setReady] = useState(false);
    const [creds, setCreds] = useState({ appId: null, userId: null, token: null });

    const colorSet = useMemo(() => ({
        '--sendbird-light-primary-500': theme.palette.text.primary,
        '--sendbird-light-primary-400': theme.palette.primary.dark,
        '--sendbird-light-primary-300': theme.palette.primary.main,
        '--sendbird-light-primary-200': theme.palette.background.paper,
        '--sendbird-light-primary-100': theme.palette.background.default,
    }), [theme]);

    useEffect(() => {
        let cancelled = false;

        const fetchCreds = async () => {
            try {
                if (app && user.loggedIn) {
                    const { data } = await api.getSendbirdAuth();
                    if (cancelled) return;
                    setCreds({
                        appId: data.payload.app_id,
                        userId: data.payload.user_id,
                        token: data.payload.token,
                    });
                    setReady(true);
                } else {
                    setReady(false);
                    setCreds({ appId: null, userId: null, token: null });
                }
            } catch (e) {
                console.error('Failed to fetch SendBird credentials:', e);
                setReady(false);
            }
        };

        fetchCreds();
        return () => { cancelled = true; };
    }, [app, user.loggedIn]);

    return (
        <SendBirdReadyContext.Provider value={ready}>
            {creds?.appId && creds?.userId && creds?.token ? (
                <SendBirdProvider
                    key={creds.userId || 'sb-guest'}
                    appId={creds.appId}
                    userId={creds.userId}
                    accessToken={creds.token}
                    colorSet={colorSet}
                >
                    {children}
                </SendBirdProvider>
            ) : (
                children
            )}
        </SendBirdReadyContext.Provider>
    );
};

export const useSendBirdReady = () => useContext(SendBirdReadyContext);

SendBirdService.propTypes = {
    children: PropTypes.node,
    sx: PropTypes.object,
};

export default SendBirdService;
